import { Card, CardHeader, Divider, Grid, Paper, SxProps, Theme, Typography, alpha, useTheme } from "@mui/material";
import React, { useEffect, useState } from "react";
import { WSHeaders } from "../../../commonFunctions/HeaderConfig/HeaderConfig";
import { getComponentProps, unRegisterComponent, updateOrRegisterComponent } from "../../../StateManagement/RegisterComponent";
import WSBackButton from "../../../Components/BackButton/BackButton";

interface IReportHeaderProps {
    title: string;
    subtitle?: string;
    type?: 'simple' | 'card' | 'light' | 'gradient';
    children?: React.ReactNode;
    isBackButton?: boolean;
    url?: string;
    id?: WSHeaders | any;
    isDivider?: boolean;
    headerSx?: SxProps<Theme>;
    action?: React.ReactNode;
    color?: 'primary' | 'secondary' | 'success' | 'info' | 'warning' | 'error';
}

export const ReportHeader: React.FC<IReportHeaderProps> = React.memo((props) => {
    const theme = useTheme();

    const [newUrl, setUrl] = useState<string | undefined>(props?.url);
    const [hasBackButton, setBackButton] = useState<boolean | undefined>(props?.isBackButton);

    // Register the component with initial props
    useEffect(() => {
        updateOrRegisterComponent(props?.id, props);
    }, []);
    
    useEffect(() => () => {
        unRegisterComponent(props?.id);
    }, []);
    
    // Handle updates from other components
    useEffect(() => {
        const propsed = getComponentProps(props?.id);
        if (propsed) {
            setUrl(propsed.url);
            setBackButton(propsed.isBackButton);
        }
    }, [props?.id]);

    const getColorValue = () => {
        const colors = {
            primary: theme.palette.primary.main,
            secondary: theme.palette.secondary.main,
            success: theme.palette.success.main,
            info: theme.palette.info.main,
            warning: theme.palette.warning.main,
            error: theme.palette.error.main
        };
        return props.color ? colors[props.color] : colors.primary;
    };

    const renderHeader = () => {
        switch (props?.type) {
            case 'card':
                return (
                    <Paper elevation={0} sx={{ backgroundColor: '#F8F9FA', borderRadius: 2 }}>
                        <Card sx={{
                            backgroundColor: '#ffffff',
                            borderRadius: 2,
                            p: 1.5,
                            width: '100%',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                            border: '1px solid #e0e0e0'
                        }}>
                            <CardHeader
                                title={
                                    <Typography variant="h5" sx={{
                                        fontWeight: 600,
                                        color: '#1a1a1a',
                                        fontSize: '1.5rem'
                                    }}>
                                        {props?.title}
                                    </Typography>
                                }
                                subheader={props.subtitle && (
                                    <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
                                        {props.subtitle}
                                    </Typography>
                                )}
                                action={props.action}
                                sx={{ p: 2 }}
                            />
                        </Card>
                        {props?.children}
                    </Paper>
                );

            case 'gradient':
                return (
                    <Grid container sx={{
                        background: `linear-gradient(135deg, ${getColorValue()} 0%, ${alpha(getColorValue(), 0.8)} 100%)`,
                        borderRadius: 2,
                        p: 3,
                        mb: 2,
                        width: '100%',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                    }}>
                        <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center' }}>
                            {hasBackButton && (
                                <WSBackButton
                                    id="btnScheduleBack" 
                                    url={newUrl} 
                                    sx={{ color: 'white' }} 
                                />
                            )}
                            <Typography variant="h5" sx={{
                                fontWeight: 600,
                                color: 'white',
                                flex: 1
                            }}>
                                {props?.title}
                            </Typography>
                            {props.action && (
                                <Grid item sx={{ ml: 'auto' }}>
                                    {props.action}
                                </Grid>
                            )}
                        </Grid>
                        {props.subtitle && (
                            <Grid item xs={12} sx={{ mt: 1 }}>
                                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                                    {props.subtitle}
                                </Typography>
                            </Grid>
                        )}
                    </Grid>
                );

            case 'simple':
                return (
                    <Grid container sx={{
                        ...props?.headerSx,
                        width: '100%',
                        borderBottom: `3px solid ${getColorValue()}`,
                        mb: 2,
                        pb: 1
                    }}>
                        <Grid item xs={12} sx={{ display: "flex", alignItems: "center" }}>
                            {hasBackButton && (
                                <WSBackButton 
                                    id="btnScheduleBack" 
                                    url={newUrl} 
                                />
                            )}
                            <Typography variant="h6" sx={{
                                fontWeight: 600,
                                color: '#333',
                                fontSize: '1.25rem',
                                flex: 1
                            }}>
                                {props?.title}
                            </Typography>
                            {props.action && (
                                <Grid item sx={{ ml: 'auto' }}>
                                    {props.action}
                                </Grid>
                            )}
                        </Grid>
                        {props.subtitle && (
                            <Grid item xs={12} sx={{ mt: 0.5 }}>
                                <Typography variant="body2" sx={{ color: '#666' }}>
                                    {props.subtitle}
                                </Typography>
                            </Grid>
                        )}
                    </Grid>
                );

            case 'light':
                return (
                    <Grid container sx={{
                        backgroundColor: alpha(getColorValue(), 0.08),
                        borderRadius: 2,
                        p: 2,
                        mb: 2,
                        width: '100%',
                        border: `1px solid ${alpha(getColorValue(), 0.2)}`
                    }}>
                        <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center' }}>
                            {hasBackButton && (
                                <WSBackButton 
                                    id="btnScheduleBack" 
                                    url={newUrl} 
                                />
                            )}
                            <Typography variant="h6" sx={{
                                fontWeight: 600,
                                color: getColorValue(),
                                flex: 1
                            }}>
                                {props?.title}
                            </Typography>
                            {props.action && (
                                <Grid item sx={{ ml: 'auto' }}>
                                    {props.action}
                                </Grid>
                            )}
                        </Grid>
                        {props.subtitle && (
                            <Grid item xs={12} sx={{ mt: 0.5 }}>
                                <Typography variant="body2" sx={{ color: '#666' }}>
                                    {props.subtitle}
                                </Typography>
                            </Grid>
                        )}
                    </Grid>
                );

            default:
                return (
                    <Typography variant="h5" sx={{
                        fontWeight: 600,
                        color: '#333',
                        mb: 2
                    }}>
                        {props?.title}
                    </Typography>
                );
        }
    };

    return (
        <>
            {renderHeader()}
            {props?.isDivider && (
                <>
                    <Divider sx={{ my: 2 }} />
                    {props?.children}
                </>
            )}
            {!props?.isDivider && props?.children}
        </>
    );
});